import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, TrendingUp, TrendingDown, X } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { apiFetch } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableTh } from '@/components/SortableTh';
import { SearchCombobox } from '@/components/SearchCombobox';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface AdjustmentRow {
  id: number;
  item_id: number;
  item_name: string;
  item_code: string | null;
  direction: 'increase' | 'decrease';
  qty: number;
  reason: string;
  notes: string | null;
  created_by: number;
  created_by_name: string | null;
  created_at: string;
}

interface SearchItem {
  id: number;
  name: string;
  code: string | null;
  category: string;
}

interface SearchResponse {
  data: { items: SearchItem[] };
}

// ─── Module-level arrays ───────────────────────────────────────────────────────

const REASON_OPTIONS: { value: string; label: string }[] = [
  { value: 'damage', label: 'Damage' },
  { value: 'loss', label: 'Loss' },
  { value: 'theft', label: 'Theft' },
  { value: 'recount', label: 'Recount' },
  { value: 'other', label: 'Other' },
];

const DIRECTION_OPTIONS: { value: 'increase' | 'decrease'; label: string }[] = [
  { value: 'increase', label: 'Increase' },
  { value: 'decrease', label: 'Decrease' },
];

// ─── Item Combobox ────────────────────────────────────────────────────────────

interface SelectedItem {
  id: number;
  name: string;
  code: string | null;
}

interface ItemComboboxProps {
  selected: SelectedItem | null;
  onSelect: (item: SelectedItem | null) => void;
  disabled?: boolean;
}

function ItemCombobox({ selected, onSelect, disabled }: ItemComboboxProps) {
  // The combobox text IS `query`. Once an item is selected the text equals its name
  // and we stop searching (so the dropdown doesn't re-open on the just-picked item).
  // Typing clears the selection (onSelect(null)), which re-enables searching — this is
  // the clear-to-search behaviour the shared SearchCombobox provides for free.
  const [query, setQuery] = useState(selected ? selected.name : '');

  const shouldSearch = query.length >= 1 && selected === null;

  const { data, isFetching } = useQuery({
    queryKey: ['item-search-adj', query],
    queryFn: () => apiFetch<SearchResponse>(`/api/search?q=${encodeURIComponent(query)}`),
    enabled: shouldSearch,
    staleTime: 10_000,
  });

  // Filter to goods only (stock adjustments only apply to physical goods).
  const items = shouldSearch ? (data?.data.items ?? []).filter((i) => i.category === 'goods') : [];

  function handleQueryChange(q: string) {
    setQuery(q);
    // Editing the text drops any prior selection so the field becomes searchable again.
    if (selected !== null) onSelect(null);
  }

  function handleSelect(item: SearchItem) {
    onSelect({ id: item.id, name: item.name, code: item.code });
    setQuery(item.name);
  }

  return (
    <SearchCombobox<SearchItem>
      value={selected ? selected.id : null}
      query={query}
      onQueryChange={handleQueryChange}
      results={items}
      getKey={(si) => si.id}
      renderResult={(si) => (
        <>
          <span className="font-medium flex-1 truncate">{si.name}</span>
          {si.code && <span className="font-mono text-muted-foreground">{si.code}</span>}
        </>
      )}
      onSelect={handleSelect}
      allowFreeText={false}
      placeholder="Search inventory items…"
      disabled={disabled}
      loading={isFetching && shouldSearch}
      promptMessage="Type to search inventory"
      emptyMessage="No goods items found."
    />
  );
}

// ─── New Adjustment Modal ──────────────────────────────────────────────────────

interface NewAdjustmentModalProps {
  onClose: () => void;
  onSaved: () => void;
}

function NewAdjustmentModal({ onClose, onSaved }: NewAdjustmentModalProps) {
  const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null);
  const [direction, setDirection] = useState<'increase' | 'decrease'>('increase');
  const [qtyStr, setQtyStr] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!selectedItem) {
      toast.error('Please select an item.');
      return;
    }
    const qty = parseFloat(qtyStr);
    if (!qtyStr || isNaN(qty) || qty <= 0) {
      toast.error('Quantity must be greater than zero.');
      return;
    }
    if (!reason) {
      toast.error('Please select a reason.');
      return;
    }

    setLoading(true);
    try {
      await apiFetch('/api/stock-adjustments', {
        method: 'POST',
        body: JSON.stringify({
          item_id: selectedItem.id,
          direction,
          qty,
          reason,
          notes: notes.trim() || undefined,
        }),
      });
      toast.success('Stock adjustment recorded');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">New Stock Adjustment</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Item picker */}
          <div className="space-y-1.5">
            <Label>
              Item (Goods only) <span className="text-destructive">*</span>
            </Label>
            <ItemCombobox selected={selectedItem} onSelect={setSelectedItem} disabled={loading} />
          </div>

          {/* Direction + Qty */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="adj-direction">Direction</Label>
              <select
                id="adj-direction"
                value={direction}
                onChange={(e) => setDirection(e.target.value as 'increase' | 'decrease')}
                disabled={loading}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                {DIRECTION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="adj-qty">
                Quantity <span className="text-destructive">*</span>
              </Label>
              <Input
                id="adj-qty"
                type="number"
                min="0.001"
                step="any"
                value={qtyStr}
                onChange={(e) => setQtyStr(e.target.value)}
                placeholder="0"
                disabled={loading}
                className="tabular-nums"
              />
            </div>
          </div>

          {/* Reason */}
          <div className="space-y-1.5">
            <Label htmlFor="adj-reason">
              Reason <span className="text-destructive">*</span>
            </Label>
            <select
              id="adj-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">Select reason…</option>
              {REASON_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="adj-notes">Notes (optional)</Label>
            <textarea
              id="adj-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
              rows={2}
              placeholder="Optional details…"
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving…' : 'Record Adjustment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function StockAdjustmentsPage() {
  const queryClient = useQueryClient();

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const {
    rows: adjustments,
    total,
    page,
    setPage,
    limit,
    setLimit,
    sort,
    dir,
    toggleSort,
    isLoading,
  } = usePaginatedList<AdjustmentRow>({
    endpoint: '/api/stock-adjustments',
    entityKey: 'adjustments',
    defaultSort: 'created',
    defaultDir: 'desc',
    extraParams: {
      reason: reason || undefined,
      from: from || undefined,
      to: to || undefined,
    },
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['adjustments'] });
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">Stock Adjustments</h1>
          {!isLoading && (
            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {total}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ExportButtons
            endpoint="/api/stock-adjustments"
            params={{
              reason: reason || undefined,
              from: from || undefined,
              to: to || undefined,
            }}
          />
          <Button size="sm" onClick={() => setModalOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            New Adjustment
          </Button>
        </div>
      </div>

      {/* Filters — one line: date range · reason (wraps on small screens) */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* 1. Date range */}
        <div className="flex items-center gap-2 text-sm shrink-0">
          <span className="text-muted-foreground">From</span>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-40 h-9 text-sm"
          />
          <span className="text-muted-foreground">To</span>
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-40 h-9 text-sm"
          />
          {(from || to) && (
            <button
              type="button"
              onClick={() => {
                setFrom('');
                setTo('');
              }}
              className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
            >
              Clear
            </button>
          )}
        </div>

        {/* 2. Reason */}
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="h-9 shrink-0 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <option value="">All reasons</option>
          {REASON_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <SortableTh
                label="Date"
                sortKey="date"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-28"
              />
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Item</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground w-24">
                Direction
              </th>
              <SortableTh
                label="Qty"
                sortKey="qty"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-20 text-right"
              />
              <th className="px-4 py-3 text-left font-medium text-muted-foreground w-28 hidden sm:table-cell">
                Reason
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell">
                Notes
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground w-32 hidden lg:table-cell">
                Created By
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && adjustments.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                  No stock adjustments found.
                </td>
              </tr>
            )}
            {adjustments.map((adj) => (
              <tr key={adj.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3 text-muted-foreground tabular-nums whitespace-nowrap">
                  {formatDate(adj.created_at)}
                </td>
                <td className="px-4 py-3">
                  <Link
                    to={`/items/${adj.item_id}`}
                    className="hover:underline underline-offset-4 font-medium"
                  >
                    {adj.item_name}
                  </Link>
                  {adj.item_code && (
                    <span className="ml-1.5 text-xs text-muted-foreground font-mono">
                      {adj.item_code}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {adj.direction === 'increase' ? (
                    <Badge variant="green">
                      <TrendingUp className="h-3 w-3 mr-1" />
                      Increase
                    </Badge>
                  ) : (
                    <Badge variant="red">
                      <TrendingDown className="h-3 w-3 mr-1" />
                      Decrease
                    </Badge>
                  )}
                </td>
                <td className="px-4 py-3 text-right tabular-nums font-medium">
                  {adj.qty % 1 === 0 ? adj.qty.toFixed(0) : adj.qty.toString()}
                </td>
                <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">
                  {capitalize(adj.reason)}
                </td>
                <td className="px-4 py-3 text-muted-foreground hidden md:table-cell max-w-xs truncate">
                  {adj.notes ?? '—'}
                </td>
                <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">
                  {adj.created_by_name ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && total > 0 && (
        <Pagination
          page={page}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      {/* Modal */}
      {modalOpen && <NewAdjustmentModal onClose={() => setModalOpen(false)} onSaved={invalidate} />}
    </div>
  );
}
